import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/authApi";

// Async thunks
export const loginAdmin = createAsyncThunk(
  "auth/loginAdmin",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      console.log("🔍 Login API Response:", response.data);
      
      const { token, user, refreshToken } = response.data;

      console.log("🔍 Extracted data:", { 
        hasToken: !!token, 
        hasUser: !!user, 
        userRole: user?.role,
        hasRefreshToken: !!refreshToken 
      });

      // Only allow admin/superadmin roles
      if (user && !["admin", "superadmin"].includes(user.role)) {
        console.log("❌ User role rejected:", user.role);
        return rejectWithValue(
          "Access denied. Admin or Super Admin role required."
        );
      }

      console.log("✅ Storing tokens in localStorage");
      // Store tokens and user in localStorage
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      // Store token expiration time (default 24 hours if not provided)
      const tokenExpiry = Date.now() + 24 * 60 * 60 * 1000; // 24 hours default
      localStorage.setItem("tokenExpiry", tokenExpiry.toString());

      console.log("✅ Login successful, returning payload");
      return { token, user, refreshToken };
    } catch (error) {
      console.log("❌ Login error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue, getState }) => {
    try {
      const refreshTokenValue = localStorage.getItem("refreshToken");

      if (!refreshTokenValue) {
        return rejectWithValue("No refresh token available");
      }

      const response = await api.post("/auth/refresh", {
        refreshToken: refreshTokenValue,
      });

      const { accessToken, user } = response.data;
      const newToken = accessToken || response.data.token;

      if (!newToken) {
        return rejectWithValue("No access token in refresh response");
      }

      // Update stored tokens
      localStorage.setItem("token", newToken);
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }

      // Update token expiration time
      const tokenExpiry = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem("tokenExpiry", tokenExpiry.toString());

      return { token: newToken, user: user || getState().auth.user };
    } catch (error) {
      // If refresh fails, clear tokens and force re-login
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("tokenExpiry");
      return rejectWithValue(
        error.response?.data?.message || "Token refresh failed"
      );
    }
  }
);

export const logoutAdmin = createAsyncThunk(
  "auth/logoutAdmin",
  async (_, { rejectWithValue }) => {
    try {
      // Call logout API endpoint
      try {
        await api.post("/auth/logout");
      } catch (apiError) {
        // Even if API call fails, clear local storage
        console.warn(
          "Logout API call failed, clearing local storage anyway:",
          apiError
        );
      }

      // Clear local storage
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("tokenExpiry");

      return true;
    } catch (error) {
      // Clear storage even on error
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("tokenExpiry");
      return rejectWithValue("Logout failed");
    }
  }
);

export const requestEmailVerification = createAsyncThunk(
  "auth/requestEmailVerification",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/verify-email/request");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to request email verification"
      );
    }
  }
);

export const confirmEmailVerification = createAsyncThunk(
  "auth/confirmEmailVerification",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/verify-email/confirm", { token });
      // Update user in state if verification successful
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      user.emailVerified = true;
      localStorage.setItem("user", JSON.stringify(user));
      return { ...response.data, user };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Email verification failed"
      );
    }
  }
);

export const initializeAuth = createAsyncThunk(
  "auth/initializeAuth",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem("token");
      const refreshTokenValue = localStorage.getItem("refreshToken");
      const userStr = localStorage.getItem("user");
      const tokenExpiry = localStorage.getItem("tokenExpiry");

      if (token && userStr) {
        const user = JSON.parse(userStr);

        // Verify user has admin role
        if (!["admin", "superadmin"].includes(user.role)) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("tokenExpiry");
          return null;
        }

        // Check if token is expired or about to expire (within 5 minutes)
        const expiryTime = tokenExpiry ? parseInt(tokenExpiry) : null;
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;

        if (expiryTime && now >= expiryTime - fiveMinutes) {
          // Token is expired or about to expire, try to refresh
          if (refreshTokenValue) {
            try {
              const refreshResult = await dispatch(refreshToken());
              if (refreshResult.type === "auth/refreshToken/fulfilled") {
                return {
                  token: refreshResult.payload.token,
                  user: refreshResult.payload.user,
                  refreshToken: refreshTokenValue,
                  tokenExpiry: Date.now() + 24 * 60 * 60 * 1000,
                };
              } else {
                // Refresh failed, clear storage
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("user");
                localStorage.removeItem("tokenExpiry");
                return null;
              }
            } catch (refreshError) {
              // Refresh failed, clear storage
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("user");
              localStorage.removeItem("tokenExpiry");
              return null;
            }
          } else {
            // No refresh token, clear storage
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("tokenExpiry");
            return null;
          }
        }

        return {
          token,
          user,
          refreshToken: refreshTokenValue || null,
          tokenExpiry: expiryTime ? parseInt(expiryTime) : null,
        };
      }

      return null;
    } catch (error) {
      return rejectWithValue("Failed to initialize auth");
    }
  }
);

const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
  tokenExpiry: null,
  isRefreshing: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setAuth: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || null;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.tokenExpiry = null;
    },
    setTokenExpiry: (state, action) => {
      state.tokenExpiry = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken || null;
        state.isAuthenticated = true;
        state.tokenExpiry = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
        state.error = null;
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.token = null;
        state.refreshToken = null;
      })

      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isRefreshing = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.tokenExpiry = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isRefreshing = false;
        state.isAuthenticated = false;
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.tokenExpiry = null;
        // Don't set error here to avoid showing refresh errors to user
      })

      // Logout
      .addCase(logoutAdmin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutAdmin.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.tokenExpiry = null;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(logoutAdmin.rejected, (state, action) => {
        // Clear state even on error
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.tokenExpiry = null;
        state.isLoading = false;
        state.error = action.payload;
      })

      // Email Verification
      .addCase(requestEmailVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestEmailVerification.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(requestEmailVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(confirmEmailVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmEmailVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.user) {
          state.user = action.payload.user;
          localStorage.setItem("user", JSON.stringify(action.payload.user));
        }
        state.error = null;
      })
      .addCase(confirmEmailVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;

        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken || null;
          state.tokenExpiry = action.payload.tokenExpiry || null;
          state.isAuthenticated = true;
        } else {
          state.isAuthenticated = false;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.tokenExpiry = null;
      });
  },
});

export const { clearError, setAuth, clearAuth } = authSlice.actions;
export default authSlice.reducer;
